"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { formatPrice } from "@/lib/marketplace-store"
import { Check, Loader2, CreditCard, Shield } from "@/lib/icons"
import type { ScriptPromoAd } from "./script-promo-card"
import { useTranslations } from "next-intl"
import { ProcessorLockedScreen } from "@/components/processor-locked"
import { usePatreonMode } from "@/lib/use-patreon-mode"

interface ScriptPurchaseModalProps {
  promo: ScriptPromoAd | null
  onClose: () => void
}

export function ScriptPurchaseModal({ promo, onClose }: ScriptPurchaseModalProps) {
  const t = useTranslations("scriptPurchaseModal")
  // Patreon mode: paid scripts cannot be bought while there is no payment processor (the server refuses too). Free scripts are unaffected.
  const { enabled: patreonMode } = usePatreonMode()
  const paidLocked = patreonMode && !!promo && promo.price_cents > 0
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [purchaseComplete, setPurchaseComplete] = useState(false)
  const [error, setError] = useState("")

  const handlePurchase = async () => {
    if (!promo) return

    setIsPurchasing(true)
    setError("")

    try {
      const isPaid = promo.price_cents > 0
      const res = await fetch(isPaid ? "/api/stripe/purchase-script" : "/api/marketplace/purchase", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(isPaid
          ? {
              scriptId: promo.script_id,
              tier: "play",
              successUrl: `${window.location.origin}/?purchase=success&script_id=${promo.script_id}`,
              cancelUrl: window.location.href,
            }
          : {
              script_id: promo.script_id,
              source_campaign_id: promo.campaign_id,
            }),
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || "Purchase failed")
      }

      const data = await res.json().catch(() => ({}))
      if (data.url) {
        window.location.href = data.url
        return
      }

      setPurchaseComplete(true)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsPurchasing(false)
    }
  }

  const handleClose = () => {
    setPurchaseComplete(false)
    setError("")
    onClose()
  }

  if (!promo) return null

  return (
    <Dialog open={!!promo} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        {purchaseComplete ? (
          // Success State
          <div className="text-center py-6">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-xl font-bold mb-2">{t("purchaseComplete")}</h3>
            <p className="text-muted-foreground mb-6">{t("titleHasBeenAddedToYourLibrary", { title: promo.title })}</p>
            <Button onClick={handleClose} className="w-full">
              {t("goToMyScripts")}
            </Button>
          </div>
        ) : (
          // Purchase Form
          <>
            <DialogHeader>
              <DialogTitle>{t("purchaseScript")}</DialogTitle>
              <DialogDescription>{t("getFullAccessToThisScriptWithUnlimitedPl")}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Script Info */}
              <div className="flex gap-4 p-4 bg-muted/50 rounded-lg">
                {promo.thumbnail_url && (
                  <img
                    src={promo.thumbnail_url || "/placeholder.svg"}
                    alt={promo.title}
                    className="w-20 h-28 object-cover rounded"
                  />
                )}
                <div className="flex-1">
                  <h4 className="font-semibold">{promo.title}</h4>
                  <p className="text-sm text-muted-foreground">{t("byCreatorUsername", { creatorUsername: promo.creator_username ?? "" })}</p>
                  <p className="text-lg font-bold mt-2">{formatPrice(promo.price_cents)}</p>
                </div>
              </div>

              {/* What's Included */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">{t("whatSIncluded")}</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    {t("fullScriptWithUnlimitedPlayback")}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    {t("inAppPlaybackAccess")}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    {t("addToGoonfolio")}
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-500" />
                    {t("lifetimeAccess")}
                  </li>
                </ul>
              </div>

              {/* Security Notice */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground p-3 bg-muted/30 rounded-lg">
                <Shield className="h-4 w-4" />
                <span>{t("securePaymentProcessedByStripe")}</span>
              </div>

              {paidLocked && <ProcessorLockedScreen variant="inline" />}
              {error && <p className="text-sm text-destructive text-center">{error}</p>}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 bg-transparent" onClick={handleClose}>
                {t("cancel")}
              </Button>
              <Button className="flex-1" onClick={handlePurchase} disabled={isPurchasing || paidLocked}>
                {isPurchasing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("processing")}
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    {t("payAmount", { amount: formatPrice(promo.price_cents) })}
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
